<template>
  <Dialog
    v-model:visible="visible"
    header="Update Property Location"
    :modal="true"
    :style="{ width: '550px' }"
    @hide="resetForm"
  >
    <div class="location-form">
      <!-- Current Location Display -->
      <div class="current-location-box">
        <h4>Current Location Breakdown:</h4>
        <div v-for="loc in locationBreakdown" :key="loc.type" class="location-item">
          <span class="location-label">
            <i :class="getLocationIcon(loc.type)"></i>
            {{ loc.label }}:
          </span>
          <span class="location-qty">{{ formatQuantity(loc.quantity) }}</span>
        </div>
      </div>

      <!-- Update PS Shelf (if any quantity at PS) -->
      <div v-if="quantityAtPS > 0" class="form-group">
        <label>PS Shelf Number:</label>
        <Dropdown
          v-model="form.ps_shelf_no"
          :options="shelfOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Select shelf"
          class="w-full"
        />
        <small class="help-text">
          Where is the property stored at the police station?
        </small>
      </div>

      <!-- Move to Others -->
      <div v-if="quantityAtPS > 0" class="form-group">
        <div class="checkbox-wrapper">
          <Checkbox
            v-model="moveToOthers"
            inputId="move-others"
            :binary="true"
          />
          <label for="move-others" class="checkbox-label">
            Move some quantity to "Others" location
          </label>
        </div>
      </div>

      <div v-if="moveToOthers" class="others-section">
        <div class="form-group">
          <label>Quantity to Move: *</label>
          <InputNumber
            v-model="form.quantity_to_move"
            :max="quantityAtPS"
            :min="0.001"
            :minFractionDigits="3"
            :maxFractionDigits="3"
            suffix=" g"
            class="w-full"
          />
          <small class="help-text">Maximum: {{ formatQuantity(quantityAtPS) }}</small>
        </div>

        <div class="form-group">
          <label>Location Details: *</label>
          <Textarea
            v-model="form.other_location_details"
            rows="3"
            placeholder="Specify where the property is located (e.g., Evidence locker at headquarters, Seized by NCB, etc.)"
            class="w-full"
          />
        </div>
      </div>
    </div>

    <template #footer>
      <Button
        label="Cancel"
        icon="ri-close-line"
        @click="visible = false"
        class="p-button-text"
      />
      <Button
        label="Update Location"
        icon="ri-check-line"
        @click="submitLocation"
        :loading="loading"
      />
    </template>
  </Dialog>
</template>

<script>
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import Dropdown from 'primevue/dropdown';
import Checkbox from 'primevue/checkbox';
import InputNumber from 'primevue/inputnumber';
import Textarea from 'primevue/textarea';

export default {
  name: 'UpdateLocationDialog',
  components: {
    Dialog,
    Button,
    Dropdown,
    Checkbox,
    InputNumber,
    Textarea
  },
  props: {
    property: {
      type: Object,
      required: true
    },
    locationBreakdown: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      visible: false,
      loading: false,
      moveToOthers: false,
      form: {
        ps_shelf_no: null,
        quantity_to_move: 0,
        other_location_details: ''
      },
      shelfOptions: [
        { label: 'S1', value: 'S1' },
        { label: 'S2', value: 'S2' },
        { label: 'S3', value: 'S3' },
        { label: 'S4', value: 'S4' },
        { label: 'S5', value: 'S5' },
        { label: 'S6', value: 'S6' },
        { label: 'S7', value: 'S7' },
        { label: 'S8', value: 'S8' },
        { label: 'S9', value: 'S9' },
        { label: 'S10', value: 'S10' }
      ]
    };
  },
  computed: {
    quantityAtPS() {
      const psLoc = this.locationBreakdown.find(l => l.type === 'ps');
      return psLoc ? psLoc.quantity : 0;
    }
  },
  methods: {
    open() {
      this.visible = true;
      const psLoc = this.locationBreakdown.find(l => l.type === 'ps');
      this.form.ps_shelf_no = psLoc ? psLoc.ps_shelf_no : null;
      this.moveToOthers = false;
      this.form.quantity_to_move = 0;
      this.form.other_location_details = '';
    },
    resetForm() {
      this.moveToOthers = false;
      this.form = {
        ps_shelf_no: null,
        quantity_to_move: 0,
        other_location_details: ''
      };
    },
    formatQuantity(qty) {
      if (!qty) return '0 g';
      if (qty >= 1000) {
        return `${(qty / 1000).toFixed(3)} kg`;
      }
      return `${qty.toFixed(3)} g`;
    },
    getLocationIcon(type) {
      const icons = {
        ps: 'ri-building-line',
        fsl: 'ri-flask-line',
        court: 'ri-government-line',
        destroyed: 'ri-delete-bin-line',
        others: 'ri-map-pin-line'
      };
      return icons[type] || 'ri-question-line';
    },
    async submitLocation() {
      if (this.moveToOthers) {
        if (!this.form.quantity_to_move || this.form.quantity_to_move <= 0) {
          this.$store.dispatch('error', 'Please enter a valid quantity to move');
          return;
        }
        if (this.form.quantity_to_move > this.quantityAtPS) {
          this.$store.dispatch('error', 'Quantity exceeds available amount at PS');
          return;
        }
        if (!this.form.other_location_details.trim()) {
          this.$store.dispatch('error', 'Please provide location details');
          return;
        }
      }

      this.loading = true;
      try {
        await this.$store.dispatch('post', {
          uri: 'updatePropertyLocation',
          data: {
            property_id: this.property.property_id,
            ps_shelf_no: this.form.ps_shelf_no,
            move_to_others: this.moveToOthers,
            quantity_to_move: this.moveToOthers ? this.form.quantity_to_move : 0,
            other_location_details: this.form.other_location_details
          }
        });

        this.$store.dispatch('success', 'Location updated successfully');
        this.$emit('refresh');
        this.visible = false;
      } catch (error) {
        this.$store.dispatch('error', error.response?.data?.message || 'Failed to update location');
      } finally {
        this.loading = false;
      }
    }
  }
}
</script>

<style scoped>
.location-form {
  padding: 10px 0;
}

.current-location-box {
  background: #F5F5F5;
  border-radius: 6px;
  padding: 15px;
  margin-bottom: 20px;
}

.current-location-box h4 {
  margin: 0 0 12px 0;
  font-size: 14px;
  color: #555;
}

.location-item {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #e0e0e0;
}

.location-item:last-child {
  border-bottom: none;
}

.location-label {
  color: #666;
  display: flex;
  align-items: center;
  gap: 8px;
}

.location-qty {
  font-weight: bold;
  color: #333;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
}

.checkbox-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
}

.checkbox-label {
  margin: 0;
  cursor: pointer;
  font-weight: 500;
  color: #333;
}

.others-section {
  background: #FFF3E0;
  border: 1px solid #FF9800;
  border-radius: 6px;
  padding: 15px;
  margin-top: 15px;
}

.help-text {
  display: block;
  margin-top: 5px;
  color: #666;
  font-size: 12px;
}

.w-full {
  width: 100%;
}
</style>
